<script lang="ts">
  /**
   * 可視化ステップ（card-schema.md §3.8、judge.md §4、components.md §1.7）。
   * StepPlayer でフレームを 1 手ずつ見せ、checkpoint のフレームで止めて「次の状態」を予想させる。
   * 答えると次のフレームを見せる。全 checkpoint に答えたら gradeVizStep で採点する。
   */
  import { tick } from 'svelte';
  import { gradeVizStep } from '../../lib/judge';
  import type { VizStepCard } from '../../lib/cards';
  import CardShell from '../CardShell.svelte';
  import CodeBlock from '../CodeBlock.svelte';
  import InlineMarkdown from '../InlineMarkdown.svelte';
  import StepPlayer from '../StepPlayer.svelte';
  import type { GradeResult } from '../types';
  import type { VizStepAnswer } from './answer';
  import { changedLabels, summarizeState } from './viz-format';
  import VizStateView from './VizStateView.svelte';

  interface Props {
    card: VizStepCard;
    index: number;
    total: number;
    box?: number;
    result?: GradeResult | undefined;
    answer?: VizStepAnswer | undefined;
    showUseCase?: boolean;
    /** CardShell の globalKeys（1 画面 1 カードの画面だけ true） */
    globalKeys?: boolean;
    ongrade?: ((result: GradeResult, answer: VizStepAnswer) => void) | undefined;
    onnext?: (() => void) | undefined;
    onprev?: (() => void) | undefined;
    onabort?: (() => void) | undefined;
    /** ヒントを開いたとき（level は開いた段階、1 始まり） */
    onhint?: ((level: number) => void) | undefined;
  }

  let {
    card,
    index,
    total,
    box = 0,
    result,
    answer: savedAnswer,
    showUseCase = false,
    globalKeys = false,
    ongrade,
    onnext,
    onprev,
    onabort,
    onhint,
  }: Props = $props();

  const uid = $props.id();
  let step = $state(0);
  /** checkpoint ごとの回答（未回答は null） */
  let answers = $state<(number | null)[]>([]);
  let selected = $state<number | null>(null);

  let lastKey = '';
  $effect.pre(() => {
    const key = `${card.id}:${index}`;
    if (key === lastKey) return;
    lastKey = key;
    if (savedAnswer) {
      answers = card.checkpoints.map((_, i) => savedAnswer.answers[i] ?? null);
      step = card.frames.length - 1;
    } else {
      answers = card.checkpoints.map(() => null);
      step = 0;
    }
    selected = null;
  });

  const graded = $derived(result !== undefined);
  const current = $derived(answers.findIndex((a) => a === null));
  const checkpoint = $derived(current >= 0 ? card.checkpoints[current] : undefined);
  const pauseAt = $derived(
    card.checkpoints.filter((_, i) => answers[i] === null).map((c) => c.frame),
  );
  const atCheckpoint = $derived(checkpoint !== undefined && step === checkpoint.frame);
  const answeredCount = $derived(answers.filter((a) => a !== null).length);
  const gradeState = $derived(graded ? 'graded' : answeredCount > 0 ? 'answering' : 'idle');

  const frame = (i: number) => card.frames[i];

  function submit() {
    if (graded || !checkpoint || !atCheckpoint || selected === null) return;
    // answers を書き換えると checkpoint（derived）は次の checkpoint を指すので、先に取っておく
    const cp = checkpoint;
    const next = [...answers];
    next[current] = selected;
    answers = next;
    selected = null;
    // 答えたら次のフレーム（予想した状態）を見せる
    if (cp.frame + 1 < card.frames.length) step = cp.frame + 1;
    if (next.every((a) => a !== null)) {
      const picked = next.map((a) => a ?? -1);
      const ok = gradeVizStep(card.checkpoints, picked);
      const hit = card.checkpoints.filter((c, i) => picked[i] === c.answer).length;
      const n = card.checkpoints.length;
      const r: GradeResult = ok
        ? { verdict: 'AC', message: `${n} か所の予想がすべて一致しました。` }
        : {
            verdict: 'WA',
            message: `${n} か所中 ${hit} か所で予想が一致しました。このカードはあとでもう一度出ます。`,
          };
      ongrade?.(r, { kind: 'viz-step', answers: picked });
    } else {
      void tick().then(() =>
        playerWrap?.querySelector<HTMLElement>('[data-testid="step-forward"]')?.focus(),
      );
    }
  }

  let player: ReturnType<typeof StepPlayer> | undefined = $state();
  let fieldset: HTMLFieldSetElement | undefined = $state();
  let playerWrap: HTMLDivElement | undefined = $state();

  function onshortcut(e: KeyboardEvent): boolean {
    if (graded || e.shiftKey) return false;
    if (e.key === 'ArrowRight') return player?.forward() ?? false;
    if (!atCheckpoint || !checkpoint || !/^[1-9]$/.test(e.key)) return false;
    const i = Number(e.key) - 1;
    if (i >= checkpoint.choices.length) return false;
    selected = i;
    fieldset?.querySelector<HTMLInputElement>(`input[value="${i}"]`)?.focus();
    return true;
  }
</script>

<CardShell
  {globalKeys}
  {card}
  {index}
  {total}
  state={gradeState}
  {result}
  {box}
  prompt="1 手ずつ進めて、次の状態を予想してください。"
  {showUseCase}
  canSubmit={atCheckpoint && selected !== null}
  {onshortcut}
  onsubmit={submit}
  {onnext}
  {onprev}
  {onabort}
  {onhint}
>
  {#if card.code}
    <CodeBlock code={card.code} />
  {/if}

  <div bind:this={playerWrap}>
    <StepPlayer bind:this={player} frames={card.frames} bind:step {pauseAt} label="ステップ操作">
      {#snippet children(i: number)}
        {@const f = frame(i)}
        {#if f}
          <VizStateView state={f.state} changed={changedLabels(frame(i - 1)?.state, f.state)} />
          {#if f.note}<p class="note"><InlineMarkdown text={f.note} /></p>{/if}
          <p class="visually-hidden" aria-live="polite">{summarizeState(f.state)}</p>
        {/if}
      {/snippet}
    </StepPlayer>
  </div>

  {#if !graded && checkpoint && !atCheckpoint}
    <p class="how">
      「1 手進む」（→）で進めます。予想する場面で止まります（{answeredCount + 1} / {card.checkpoints
        .length}）。
    </p>
  {/if}

  {#if !graded && checkpoint && atCheckpoint}
    <fieldset bind:this={fieldset} class="answer" data-testid="checkpoint">
      <legend>
        <span class="eyebrow">予想 {current + 1} / {card.checkpoints.length}</span>
        <InlineMarkdown text={checkpoint.prompt} />
      </legend>
      <ol class="choices" role="list">
        {#each checkpoint.choices as c, i (i)}
          <li>
            <label class="choice" class:selected={selected === i}>
              <input type="radio" name="{uid}-cp{current}" value={i} bind:group={selected} />
              <kbd aria-hidden="true">{i + 1}</kbd>
              <span class="label"><InlineMarkdown text={c} /></span>
            </label>
          </li>
        {/each}
      </ol>
    </fieldset>
  {/if}

  {#if answeredCount > 0}
    <ol class="answered" aria-label="これまでの予想" data-testid="answered">
      {#each card.checkpoints as c, i (i)}
        {@const a = answers[i]}
        {#if a !== null && a !== undefined}
          <li class:ok={a === c.answer} class:ng={a !== c.answer}>
            <span class="mark" aria-hidden="true">{a === c.answer ? '✓' : '✕'}</span>
            <span class="visually-hidden">{a === c.answer ? '一致' : '不一致'}</span>
            <span>予想 {i + 1}: <InlineMarkdown text={c.choices[a] ?? ''} /></span>
            {#if a !== c.answer}
              <span class="right"
                >→ 正しくは <InlineMarkdown text={c.choices[c.answer] ?? ''} /></span
              >
            {/if}
          </li>
        {/if}
      {/each}
    </ol>
  {/if}
</CardShell>

<style>
  .note,
  .how {
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  .answer {
    border: 1px solid var(--accent);
    border-radius: var(--radius-md);
    padding: var(--space-3) var(--space-4);
    margin: 0;
    min-width: 0;
  }
  .answer legend {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-weight: var(--fw-medium);
    padding: 0 var(--space-1);
  }
  .choices {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    margin: var(--space-2) 0 0;
  }
  .choice {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-height: var(--tap-min);
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-md);
    background: var(--surface);
    font-weight: var(--fw-regular);
    cursor: pointer;
    transition: background-color var(--dur-fast) var(--ease-out);
  }
  .choice:hover {
    background: var(--canvas);
  }
  .choice.selected {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  .choice:has(input:focus-visible) {
    outline: var(--focus-width) solid var(--focus);
    outline-offset: var(--focus-offset);
  }
  .choice input:focus-visible {
    outline: none;
  }
  .label {
    flex: 1;
  }
  .answered {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--fs-sm);
  }
  .answered li {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-2);
  }
  .mark {
    font-weight: var(--fw-bold);
  }
  .answered .ok .mark {
    color: var(--ok-text);
  }
  .answered .ng .mark {
    color: var(--err-text);
  }
  .right {
    color: var(--text-soft);
  }
</style>
